import { Grid } from "@mui/material";
import Axes from "@shared/charts/Axes";
import Tooltip from "@shared/charts/Tooltip";
import chartClasses from "@shared/charts/chart.module.css";
import { nearestItemTo } from "@shared/charts/helpers";
import mouseEventToDomPoint from "@shared/charts/mouseEventToDomPoint";
import { ShowTooltipEvent, useDebouncedEventTracking } from "@shared/charts/tracking";
import { Series, TooltipLocation } from "@shared/charts/types";
import { useChartValues } from "@shared/charts/useChartValues";
import { lightGray, lightGrayDark } from "@shared/designSystem/theme";
import { SherlockChangeDetails, SherlockMetricType } from "@shared/http/apiTypes/sherlockApiTypes";
import { max } from "d3-array";
import { ScaleLinear, ScaleTime } from "d3-scale";
import { area, curveCardinal } from "d3-shape";
import React, { useCallback, useRef, useState } from "react";
import { height, margin, width } from "./chart/dimensions";
import { sherlockChartTooltipRenderer } from "./chart/tooltipRenderer";

export type SherlockChartProps = {
  series: Series<SherlockChangeDetails[]>[];
  yAxisLabel?: string;
  children?: React.ReactNode;
  onEventClick?: (val: string) => void;
  selectedOrganization: string;
  selectedCampaign: string;
  selectedMetric: SherlockMetricType;
  isSignificant: boolean;
  currencySymbol: string;
};

function SherlockResultsChart(props: SherlockChartProps) {
  const {
    series,
    yAxisLabel,
    selectedOrganization,
    selectedCampaign,
    onEventClick,
    isSignificant,
    selectedMetric,
    currencySymbol,
  } = props;

  const { maxY, minX, maxX, x, y } = useChartValues<SherlockChangeDetails[]>(series, width, height);

  const svgRef = useRef<SVGSVGElement>(null);

  const trackEvent = useDebouncedEventTracking(5000);

  const [changeTooltip, setChangeTooltip] = useState<TooltipLocation | null>(null);
  const [changeDetails, setChangeDetails] = useState<SherlockChangeDetails[] | []>([]);

  const closestTimeToMouse =
    changeTooltip && series.length > 0 && nearestItemTo(x.invert(changeTooltip.svgX - margin.left), series[0].values).x;

  const closestValuesToMouse =
    changeTooltip && closestTimeToMouse && series.length > 0
      ? series.map(s => {
          const value = s.values.find(value => value.x.valueOf() === closestTimeToMouse.valueOf());
          return { label: s.label, value: value?.y };
        })
      : [];

  const showChangeTooltip = useCallback(
    (event: React.MouseEvent<SVGGElement, MouseEvent>, changes?: SherlockChangeDetails[]) => {
      const svg = svgRef.current;
      if (!svg) {
        return;
      }
      const { x, y } = mouseEventToDomPoint(event, svg);
      setChangeTooltip({
        svgX: x,
        svgY: y,
        mouseX: event.pageX,
        mouseY: event.pageY,
      });
      if (!changes) {
        return;
      }
      setChangeDetails(changes);
      trackEvent(
        new ShowTooltipEvent({
          "Graph Name": "Sherlock Results",
          "Organization Id": selectedOrganization,
          "Campaign Id": selectedCampaign,
          "Event IDs": changes.map(change => change.sherlock_change_id).join(","),
        }),
      );
    },
    [selectedOrganization, selectedCampaign, trackEvent],
  );

  const hideChangeTooltip = useCallback(() => {
    setChangeDetails([]);
    setChangeTooltip(null);
  }, []);

  return (
    <Grid container marginBottom={10}>
      {changeTooltip && (
        <Tooltip
          side={changeTooltip.svgX > width / 2 ? "left" : "right"}
          pageX={changeTooltip.mouseX}
          pageY={changeTooltip.mouseY}
        >
          {sherlockChartTooltipRenderer(
            closestTimeToMouse as Date,
            closestValuesToMouse,
            selectedMetric,
            changeDetails as SherlockChangeDetails[],
            currencySymbol,
          )}
        </Tooltip>
      )}
      <svg
        className={chartClasses.svg}
        viewBox={`0 0 ${width + margin.left + margin.right} ${height + margin.bottom + margin.top}`}
        ref={svgRef}
        onMouseMove={showChangeTooltip}
        onClick={showChangeTooltip}
        onMouseLeave={hideChangeTooltip}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          <Axes
            {...{
              x,
              y,
              minX,
              maxX,
              max,
              maxY,
              yAxisLabel,
              renderYTickGuidelines: true,
            }}
          />

          {series.map(s => {
            const predictedData = s.label === "Predicted Data";
            const fill = predictedData && !isSignificant ? "none" : undefined;
            return (
              <AreaGraph
                key={s.label}
                x={x}
                y={y}
                series={s}
                fill={fill}
                onEventClick={onEventClick ? onEventClick : () => {}}
                onMouseMove={showChangeTooltip}
                onMouseLeave={hideChangeTooltip}
              />
            );
          })}

          {closestTimeToMouse && (
            <line
              style={{ transform: `translate(${x(closestTimeToMouse)}px)` }}
              className={chartClasses.tooltipHighlightLine}
              x1={0}
              x2={0}
              y1={y(0)!}
              y2={y(maxY)}
            />
          )}

          {closestValuesToMouse.length > 0 && closestValuesToMouse[0].value && (
            <line
              style={{
                transform: `translate(0, ${y(closestValuesToMouse[0].value)}px)`,
              }}
              className={chartClasses.tooltipHighlightLine}
              x1={0}
              x2={x(maxX)}
              y1={0}
              y2={0}
            />
          )}
        </g>
      </svg>
    </Grid>
  );
}

const AreaGraph = React.memo(
  ({
    x,
    y,
    series,
    fill,
    onEventClick,
    onMouseMove,
    onMouseLeave,
    pathProps,
  }: {
    series: Series<SherlockChangeDetails[]>;
    x: ScaleTime<number, number>;
    y: ScaleLinear<number, number>;
    onEventClick?: (val: string | null) => void;
    onMouseMove?: (
      event: React.MouseEvent<SVGGElement, MouseEvent>,
      sherlockChangeDetails: SherlockChangeDetails[],
    ) => void;
    onMouseLeave?: React.MouseEventHandler<SVGGElement>;
    fill?: string;
    pathProps?: React.SVGProps<SVGPathElement>;
  }) => {
    if (series.values.length === 0) return;

    const pathD = React.useMemo(() => {
      const areaGen = area<{ x: number; y: number; y1: number }>()
        .x(d => d.x)
        .y(d => d.y)
        .y1(d => d.y1)
        .curve(curveCardinal.tension(0.5));

      const values = series.values.map(i => ({
        x: x(new Date(i.x).getTime())!,
        y: y(0)!,
        y1: y(i.y)!,
      }));

      return areaGen(values)!;
    }, [series.values, x, y]);

    const foundEvents = series.values.filter(value => value.events !== undefined);

    return (
      <>
        <path
          fill={fill || series.color}
          stroke={series.color}
          d={pathD!}
          transform="translate(1, -1)"
          {...pathProps}
          strokeDasharray={fill ? "5,5" : "none"}
        />

        {foundEvents.length > 0
          ? foundEvents.map((item, idx) => {
              const eventX = x(new Date(item.x).getTime());
              const eventY = (() => {
                const found = series.values.find(d => new Date(d.x).getTime() === new Date(item.x).getTime());
                return found ? y(found.y) : null;
              })();

              if (eventX == null || eventY == null) return null;
              if (!item.events) return;

              const changeId = item.events[0].sherlock_change_id;
              const changes = item.events;

              return (
                <circle
                  key={idx}
                  cx={eventX}
                  cy={eventY}
                  r={8}
                  fill={changeId ? lightGrayDark : lightGray}
                  stroke={lightGrayDark}
                  strokeWidth={2}
                  onClick={() => onEventClick && onEventClick(changeId)}
                  onMouseMove={event => onMouseMove && onMouseMove(event, changes)}
                  onMouseLeave={onMouseLeave}
                  className={changeId ? chartClasses.circle : undefined}
                />
              );
            })
          : null}
      </>
    );
  },
);

export default React.memo(SherlockResultsChart);
